import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { userApi } from '../config';
import {
  ArrowLeft,
  ShieldCheck,
  User,
  LockKeyhole,
  Mail,
  Phone,
  MapPin,
  Leaf,
  Clock3,
  Calendar,
  CheckCircle,
  Video,
} from 'lucide-react';

const loadRazorpayScript = async (): Promise<boolean> => {
  if ((window as any).Razorpay) return true;
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const BookingStep3: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const classId = searchParams.get('classId') || '';
  const plan = searchParams.get('plan') || '';
  const amtString = searchParams.get('totalAmt') || searchParams.get('amt') || '0';
  const amount = Number(amtString.replace(/[^0-9.-]+/g, ''));
  const platformFeePercent = 3;
  const platformFee = Number(((amount * platformFeePercent) / 100).toFixed(2));
  const payableAmount = Number((amount + platformFee).toFixed(2));
  const formatAmount = (value: number) =>
    value.toLocaleString('en-IN', {
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2,
    });
  const mode = searchParams.get('mode') || 'online';
  const date = searchParams.get('date') || '';
  const time = searchParams.get('time') || '';

  const [classDetails, setClassDetails] = useState<any>(location.state?.classDetails || null);
  const [instructor, setInstructor] = useState<any>(location.state?.instructor || null);
  const [loading, setLoading] = useState(!location.state?.classDetails);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    notes: '',
  });
  const [errors, setErrors] = useState<Partial<Record<'name' | 'phone' | 'email' | 'city', string>>>({});
  const [agreed, setAgreed] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isRecurringClass = classDetails?.classType === 'recurring';
  const isLongTermPlan = ['monthly', 'yearly'].includes(plan.toLowerCase());
  const isDateOnlyPlan = isRecurringClass || isLongTermPlan;

  const instructorName =
    instructor?.name ||
    [instructor?.firstname, instructor?.lastname].filter(Boolean).join(' ') ||
    instructor?.vendorName ||
    'Instructor';

  useEffect(() => {
    const loadDetails = async () => {
      setLoading(true);
      try {
        const res = await userApi.get<any>(`/vendor/api/v1/services/service/${classId}`);
        if (res.data && res.data.data) {
          setClassDetails(res.data.data);
          const vendorRes = await userApi.get<any>(`/vendor/${res.data.data.mentorId}`);
          if (vendorRes.data && vendorRes.data.vendor) {
            setInstructor(vendorRes.data.vendor);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (classId && !classDetails) {
      loadDetails();
    } else if (classDetails) {
      setLoading(false);
    }
  }, [classId, classDetails]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-[#15302B]/20 border-t-[#15302B] rounded-full animate-spin" />
          <p className="text-xs font-semibold text-[#15302B]">Loading checkout...</p>
        </div>
      </div>
    );
  }

  const validateForm = () => {
    const nextErrors: Partial<Record<'name' | 'phone' | 'email' | 'city', string>> = {};

    if (!formData.name.trim()) nextErrors.name = 'Full name is required';
    if (!formData.phone.trim()) nextErrors.phone = 'Mobile number is required';
    if (!formData.email.trim()) nextErrors.email = 'Email address is required';
    if (!formData.city.trim()) nextErrors.city = 'City is required';

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handlePayment = async () => {
    if (!validateForm()) {
      toast.error('Please fill all required details');
      return;
    }
    if (!agreed) {
      toast.error('Please agree to the terms');
      return;
    }

    setIsSubmitting(true);

    const endpoint = '/book-class';
    const verifyEndpoint = '/verify-payment';
    const normalizedEmail = formData.email.trim().toLowerCase();
    const normalizedPhone = formData.phone.replace(/\D/g, '');
    const normalizedName = formData.name.trim();

    const resolvedTimeSlot = (time && time.trim()) || (isDateOnlyPlan ? 'Flexible' : 'Flexible');

    // Free booking flow
    if (amount === 0) {
      try {
        const payload = {
          classId,
          className: classDetails?.classTitle || 'Session',
          name: normalizedName,
          email: normalizedEmail,
          phone: normalizedPhone,
          age: '25',
          preferredTimeSlot: resolvedTimeSlot,
          bookingDate: date || new Date().toISOString(),
          totalAmount: amount,
          selectedPricing: { type: plan },
          vendorId: instructor?._id,
          isDemo: false,
          selectedDates: date ? date.split(' | ') : [],
        };

        const response = await userApi.post<any>(endpoint, payload);
        if (response.data && response.data.success !== false && (response.data.bookingId || response.data.message)) {
          toast.success('Booking confirmed!');
          const successState = {
            bookingId:
              response.data.bookingId ||
              response.data.data?.bookingId ||
              `MTF-${Math.random().toString(36).substring(2, 9).toUpperCase()}`,
            amount: '0',
            serviceName: classDetails?.classTitle || 'Session',
            date: date || new Date().toLocaleDateString('en-GB'),
            duration: 'Flexible',
            location: mode === 'online' ? 'Online' : classDetails?.venueAddress || 'Offline',
            customerName: formData.name,
            customerEmail: formData.email,
            customerPhone: formData.phone,
          };
          try {
            sessionStorage.setItem('last_payment_success_state', JSON.stringify(successState));
          } catch (e) {}
          navigate('/payment-success', { state: successState });
        } else {
          toast.error('Failed to book session');
        }
      } catch (e: any) {
        console.error(e);
        toast.error(e?.response?.data?.message || 'An error occurred');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Paid flow
    try {
      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) {
        toast.error('Failed to load payment gateway');
        setIsSubmitting(false);
        return;
      }

      const payload = {
        classId,
        className: classDetails?.classTitle || 'Session',
        name: normalizedName,
        email: normalizedEmail,
        phone: normalizedPhone,
        age: '25',
        preferredTimeSlot: resolvedTimeSlot,
        bookingDate: date || new Date().toISOString(),
        totalAmount: payableAmount,
        selectedPricing: { type: plan },
        vendorId: instructor?._id,
        isDemo: false,
        selectedDates: date ? date.split(' | ') : [],
      };

      const response = await userApi.post<any>(endpoint, payload);

      if (!response.data || (!response.data.data?.razorpayOrderId && !response.data.razorpayOrderId)) {
        toast.error(response.data?.message || 'Could not initiate payment');
        setIsSubmitting(false);
        return;
      }

      const orderData = response.data.data || response.data;

      const options = {
        key: orderData.razorpayKeyId || process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_Dlhggc4TFEcwkN',
        order_id: orderData.razorpayOrderId,
        amount: orderData.amount,
        currency: orderData.currency || 'INR',
        name: 'MetaFit Wellness',
        description: `${classDetails?.classTitle || 'Session'} - Booking`,
        handler: async (paymentResponse: any) => {
          setIsSubmitting(true);
          try {
            const verificationResponse = await userApi.post<any>(verifyEndpoint, {
              razorpay_payment_id: paymentResponse.razorpay_payment_id,
              razorpay_order_id: paymentResponse.razorpay_order_id,
              razorpay_signature: paymentResponse.razorpay_signature,
              pendingBookingData: orderData.pendingBookingData,
            });

            if (verificationResponse.data && verificationResponse.data.success !== false) {
              toast.success('Payment successful! Session booked.');
              const successState = {
                bookingId:
                  verificationResponse.data.bookingId ||
                  verificationResponse.data.data?.bookingId ||
                  orderData.pendingBookingData?.bookingId ||
                  `MTF-${Math.random().toString(36).substring(2, 9).toUpperCase()}`,
                amount: payableAmount.toString(),
                serviceName: classDetails?.classTitle || 'Session',
                date: date || new Date().toLocaleDateString('en-GB'),
                duration: 'Flexible',
                location: mode === 'online' ? 'Online' : classDetails?.venueAddress || 'Offline',
                customerName: formData.name,
                customerEmail: formData.email,
                customerPhone: formData.phone,
              };
              try {
                sessionStorage.setItem('last_payment_success_state', JSON.stringify(successState));
              } catch (e) {}
              navigate('/payment-success', { state: successState });
            } else {
              toast.error(verificationResponse.data?.message || 'Payment verification failed');
              setIsSubmitting(false);
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error('Payment verification failed');
            setIsSubmitting(false);
          }
        },
        prefill: {
          name: normalizedName,
          email: normalizedEmail,
          contact: normalizedPhone,
        },
        theme: {
          color: '#3F6652',
        },
        modal: {
          ondismiss: () => {
            toast.error('Payment cancelled');
            setIsSubmitting(false);
          },
        },
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.open();
    } catch (e: any) {
      console.error(e);
      toast.error(e?.response?.data?.message || 'Payment initialization failed');
      setIsSubmitting(false);
    }
  };

  const formattedDate = date ? new Date(date).toLocaleDateString('en-GB') : '';

  return (
    <div className="relative min-h-screen bg-[#FAF7F2] text-[#15302B] antialiased">
      {/* Background ambient glow */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(ellipse_at_top,rgba(21,48,43,0.05),transparent_70%)]" />

      <main className="relative mx-auto max-w-[1180px] px-4 pt-4 pb-16 sm:px-6 sm:pt-6 sm:pb-20 lg:px-8">
        {/* Back Button */}
        <div className="mb-3 sm:mb-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="group inline-flex items-center gap-1.5 rounded-full border border-[#15302B]/15 bg-white px-3.5 py-1.5 text-xs font-semibold text-[#15302B] shadow-2xs transition-all hover:bg-[#F2F8F4] hover:shadow-sm cursor-pointer"
          >
            <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-1" />
            <span>Back</span>
          </button>
        </div>

        {/* Page Header */}
        <div className="mb-5 sm:mb-6 border-b border-[#15302B]/10 pb-4 sm:pb-5">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
            <div>
              <h1 className="font-serif text-xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#15302B]">
                Complete Your Booking
              </h1>
            </div>

            <div className="hidden sm:inline-flex items-center gap-2 rounded-full border border-[#15302B]/12 bg-white px-3.5 py-1.5 text-xs font-semibold text-[#2D6A4F] shadow-2xs self-start sm:self-auto shrink-0">
              <ShieldCheck className="h-4 w-4 text-[#2D6A4F]" />
              <span>100% Secure Checkout</span>
            </div>
          </div>
        </div>

        {/* 2-Column Responsive Layout (Matches retreat booking preview) */}
        <div className="grid items-stretch gap-6 lg:grid-cols-2">
          {/* Left Column: Guest Details */}
          <div className="lg:col-span-1 flex flex-col">
            <div className="h-full flex flex-col justify-between overflow-hidden rounded-2xl border border-[#15302B]/10 bg-white shadow-sm transition-shadow hover:shadow-md">
              {/* Card Header */}
              <div className="flex items-center justify-between border-b border-[#15302B]/8 bg-[#FAF7F2]/60 px-4 py-3 sm:px-5 sm:py-3.5">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-[#15302B] text-white shadow-xs">
                    <User className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h2 className="font-serif text-sm sm:text-lg font-bold text-[#15302B]">
                      Primary Guest Details
                    </h2>
                  </div>
                </div>
                <span className="hidden items-center gap-1 text-[11px] font-medium text-[#3F6652] sm:inline-flex">
                  <LockKeyhole className="h-3 w-3" />
                  Encrypted
                </span>
              </div>

              {/* Card Form */}
              <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between">
                <div className="space-y-4">
                  {/* Full Name */}
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-[#15302B]">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#3F6652]" />
                      <input
                        required
                        type="text"
                        autoComplete="name"
                        value={formData.name}
                        onChange={(e) => {
                          setFormData({ ...formData, name: e.target.value });
                          if (errors.name) setErrors({ ...errors, name: undefined });
                        }}
                        className={`w-full rounded-xl border bg-white py-2.5 pl-10 pr-4 text-xs font-medium text-[#15302B] shadow-2xs outline-none transition-all placeholder:text-[#94A3B8] focus:border-[#3F6652] focus:ring-2 focus:ring-[#3F6652]/20 ${
                          errors.name ? 'border-red-500' : 'border-[#15302B]/15'
                        }`}
                        placeholder="e.g. Priya Sharma"
                      />
                    </div>
                    {errors.name && <p className="text-[11px] text-red-500 mt-1">{errors.name}</p>}
                  </div>

                  {/* Mobile Number */}
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-[#15302B]">
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#3F6652]" />
                      <input
                        required
                        type="tel"
                        autoComplete="tel"
                        value={formData.phone}
                        onChange={(e) => {
                          setFormData({ ...formData, phone: e.target.value });
                          if (errors.phone) setErrors({ ...errors, phone: undefined });
                        }}
                        className={`w-full rounded-xl border bg-white py-2.5 pl-10 pr-4 text-xs font-medium text-[#15302B] shadow-2xs outline-none transition-all placeholder:text-[#94A3B8] focus:border-[#3F6652] focus:ring-2 focus:ring-[#3F6652]/20 ${
                          errors.phone ? 'border-red-500' : 'border-[#15302B]/15'
                        }`}
                        placeholder="+91 98765 43210"
                      />
                    </div>
                    {errors.phone && <p className="text-[11px] text-red-500 mt-1">{errors.phone}</p>}
                  </div>

                  {/* Email Address */}
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-[#15302B]">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#3F6652]" />
                      <input
                        required
                        type="email"
                        autoComplete="email"
                        value={formData.email}
                        onChange={(e) => {
                          setFormData({ ...formData, email: e.target.value });
                          if (errors.email) setErrors({ ...errors, email: undefined });
                        }}
                        className={`w-full rounded-xl border bg-white py-2.5 pl-10 pr-4 text-xs font-medium text-[#15302B] shadow-2xs outline-none transition-all placeholder:text-[#94A3B8] focus:border-[#3F6652] focus:ring-2 focus:ring-[#3F6652]/20 ${
                          errors.email ? 'border-red-500' : 'border-[#15302B]/15'
                        }`}
                        placeholder="priya@example.com"
                      />
                    </div>
                    {errors.email && <p className="text-[11px] text-red-500 mt-1">{errors.email}</p>}
                  </div>

                  {/* City */}
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-[#15302B]">
                      City <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#3F6652]" />
                      <input
                        required
                        type="text"
                        value={formData.city}
                        onChange={(e) => {
                          setFormData({ ...formData, city: e.target.value });
                          if (errors.city) setErrors({ ...errors, city: undefined });
                        }}
                        className={`w-full rounded-xl border bg-white py-2.5 pl-10 pr-4 text-xs font-medium text-[#15302B] shadow-2xs outline-none transition-all placeholder:text-[#94A3B8] focus:border-[#3F6652] focus:ring-2 focus:ring-[#3F6652]/20 ${
                          errors.city ? 'border-red-500' : 'border-[#15302B]/15'
                        }`}
                        placeholder="e.g. Pune"
                      />
                    </div>
                    {errors.city && <p className="text-[11px] text-red-500 mt-1">{errors.city}</p>}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Order Summary Sidebar */}
          <aside className="lg:col-span-1 flex flex-col">
            <div className="h-full flex flex-col justify-between overflow-hidden rounded-2xl border border-[#15302B]/10 bg-white shadow-sm transition-shadow hover:shadow-md">
              <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between space-y-4">
                <div className="space-y-3.5">
                  {/* Session Info Header */}
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-[#3F6652]">
                        <Leaf className="h-2.5 w-2.5" /> Order Summary
                      </span>
                      <span className="rounded-full bg-[#3F6652]/10 px-2 py-0.5 text-[9px] font-semibold text-[#15302B]">
                        Verified Session
                      </span>
                    </div>

                    <h3 className="mt-1 font-serif text-[14px] sm:text-[15px] font-bold text-[#15302B] leading-tight">
                      {classDetails?.classTitle || 'Wellness Session'}
                    </h3>

                    <div className="mt-2 space-y-1 text-[11px] text-[#4A5568]">
                      {/* Practitioner */}
                      <p className="flex items-center gap-1.5">
                        <User className="h-3 w-3 text-[#3F6652] shrink-0" />
                        <span>
                          Practitioner: <strong className="text-[#15302B]">{instructorName}</strong>
                        </span>
                      </p>

                      {/* Mode */}
                      <p className="flex items-center gap-1.5">
                        {mode === 'online' ? (
                          <Video className="h-3 w-3 text-emerald-600 shrink-0" />
                        ) : (
                          <MapPin className="h-3 w-3 text-[#E05638] shrink-0" />
                        )}
                        <span className="capitalize">
                          {mode === 'online'
                            ? 'Online • Google Meet / Zoom'
                            : classDetails?.venueAddress || 'Offline Studio Session'}
                        </span>
                      </p>

                      {/* Schedule / Date & Time */}
                      {(date || time) && (
                        <p className="flex items-center gap-1.5 text-[#15302B] font-medium">
                          <Calendar className="h-3 w-3 text-[#3F6652] shrink-0" />
                          <span>
                            Starts: <strong className="text-[#2D6A4F]">{formattedDate}</strong>
                            {time && !isDateOnlyPlan ? ` • ${time}` : ''}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="border-t border-[#15302B]/8" />

                  {/* Price Breakdown */}
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between text-[#4A5568]">
                      <span>{plan} plan</span>
                      <span className="font-mono font-medium">₹{formatAmount(amount)}</span>
                    </div>

                    <div className="flex justify-between text-[#4A5568]">
                      <span>Platform fee ({platformFeePercent}%)</span>
                      <span className="font-mono font-medium">₹{formatAmount(platformFee)}</span>
                    </div>

                    <div className="border-t border-[#15302B]/8 pt-3 flex items-baseline justify-between">
                      <div>
                        <p className="text-xs font-bold text-[#15302B]">Total Payable</p>
                        <p className="text-[10px] text-[#718096]">Inclusive of platform fees</p>
                      </div>
                      <p className="font-serif text-lg sm:text-xl font-bold text-[#15302B]">
                        ₹{formatAmount(payableAmount)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 mt-auto pt-2">
                  {/* Terms Checkbox */}
                  <div className="flex items-start gap-2 bg-[#FAF7F2] p-3 rounded-xl border border-[#15302B]/8">
                    <input
                      type="checkbox"
                      id="terms-checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#15302B] focus:ring-[#15302B] cursor-pointer accent-[#15302B]"
                    />
                    <label
                      htmlFor="terms-checkbox"
                      className="text-xs text-[#15302B] select-none cursor-pointer leading-tight"
                    >
                      I agree to MetaFit's{' '}
                      <a
                        href="/policy/terms-conditions"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-bold underline hover:text-[#2C4A3B]"
                      >
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a
                        href="/policy/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-bold underline hover:text-[#2C4A3B]"
                      >
                        Privacy Policy
                      </a>
                      .
                    </label>
                  </div>

                  {/* Payment Button */}
                  <button
                    type="button"
                    onClick={handlePayment}
                    disabled={isSubmitting || !agreed}
                    className="w-full flex items-center justify-center gap-2 rounded-full bg-black py-2.5 px-5 text-xs sm:text-[13px] font-semibold text-white shadow-sm transition-all hover:bg-neutral-800 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <LockKeyhole className="h-4 w-4" />
                    {isSubmitting
                      ? 'Processing...'
                      : payableAmount === 0
                        ? 'Confirm Booking'
                        : `Pay ₹${formatAmount(payableAmount)} & confirm →`}
                  </button>

                  <div className="flex items-center justify-center gap-1.5 text-[11px] text-[#4A5568]">
                    <CheckCircle className="h-3.5 w-3.5 text-[#3F6652]" />
                    <span>Instant confirmation after payment</span>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default BookingStep3;
